import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getIncomeAttributesBySnapshotId, putIncomeAttributesBySnapshotId } from '../../../redux/actions/incomeAttributes/incomeAttributes';
import { getLookups } from '../../../redux/actions/lookups/lookups';
import { IncomeAttributeState } from '../../../redux/reducers/getIncomeAttributes/getIncomeAttributesReducer';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer';
import ExpenseTab from './ExpenseTab';
import Asterisk from '../../../components/common/Asterisk/Asterisk';
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion';
import AccordionItem, { DefaultAccordionProviderOptions } from '../../../components/common/AccordionItem/AccordionItem';
import Total from '../../../components/common/Total/Total';
import "../PIE.css";
import { useNavigation } from '../../../utils/context/NavigationContext';
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages';
import { useFormErrorContext } from '../../../utils/context/FormErrorContext';
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from '../../../utils/previousIncomeAttributes';
import { MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID } from '../../../redux/actionTypes/actionsTypes';
import PreviousCampaignIncomeAttributes from '../../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes';
import { selectGetIncomeAttributes, selectLookups, selectMenu, selectPreviousIncomeAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths';
import IncomeAttributeLookupObject from '../../../types/lookups/IncomeAttributeLookupObject';

export default function ExpenseContainer(): JSX.Element {
  const { t } = useTranslation(['navigation', 'pie']);
  const dispatch = useDispatch();
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
  const incomeAttributesState: IncomeAttributeState = useSelector(selectGetIncomeAttributes);
  const previousIncomeAttributesState: IncomeAttributeState = useSelector(selectPreviousIncomeAttributes)
  const saveResultState: SaveResultState = useSelector(selectSaveResult);
  const menuState = useSelector(selectMenu);
  const tabList = menuState.activeItem.subItems;
  const lookupType = profileOverview.profile.profile_type_code.toLowerCase() + "-income-attributes";
  const lookupState = useSelector(selectLookups);
  const saveData: SaveDataState = useSelector(selectSaveData);
  const { direction } = useNavigation()
  const { errors, clearErrors } = useFormErrorContext()
  const [totalOperatingExpense, setTotalOperatingExpense] = useState<number | undefined>(undefined);
  const [totalNonOperatingExpense, setTotalNonOperatingExpense] = useState<number | undefined>(undefined);
  const [previousTotalOperatingExpenses, setPreviousTotalOperatingExpenses] = useState<Record<string, number>>({});
  const [previousTotalNonOperatingExpenses, setPreviousTotalNonOperatingExpenses] = useState<Record<string, number>>({});
  const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
  const nonOperatingKeys = new Set([
    "nonOperatingIncomeExpenses",
    "GLF.nonOperatingIncomeExpenses",
  ]);
  const currentCampaignYear = getCurrentCampaignYear()

  const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)

  const findAttributeData = (attributeCode: string) => {
    let incomeAttributesData: Record<string, unknown> | undefined = undefined;
    if (incomeAttributesState.data) {
      incomeAttributesData = (incomeAttributesState.data as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
    }
    return incomeAttributesData;
  };

  const findPreviousAttributeData = (attributeCode: string, year: string) => {
    let incomeAttributesData: Record<string, unknown> | undefined = undefined;
    const sessionStoragePreviousSnapshotData = sessionStorage.getItem(isInternal ? "previousSnapshotDataInternal" + year : "previousSnapshotData")
    if (sessionStoragePreviousSnapshotData !== null && sessionStoragePreviousSnapshotData !== undefined) {
      let previousSnapshotData = { incomeAttribute: [] }
      previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotData)
      if (previousSnapshotData.incomeAttribute) {
        incomeAttributesData = (previousSnapshotData.incomeAttribute as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
      }
    }
    return incomeAttributesData;
  }


  const getIncomeAttributes = () => {
    if ((!incomeAttributesState.loading && !incomeAttributesState.success)
      || incomeAttributesState.snapshotId !== Number(sessionStorage.getItem('currentSnapshotId') as string)) {
      dispatch(getIncomeAttributesBySnapshotId(Number(sessionStorage.getItem('currentSnapshotId') as string)));
    }

    const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

    if (campaign && ((!previousIncomeAttributesState.loading && !previousIncomeAttributesState.success) || previousIncomeAttributesState.snapshotId !== campaign.currentSnapshotId)) {
      dispatch(getIncomeAttributesBySnapshotId(campaign.currentSnapshotId, MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID))
    }
  }


  useEffect(() => {
    getIncomeAttributes()
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (saveData.saving) {
      clearErrors()
      const incomeAttributes = (incomeAttributesState.data as Record<string, unknown>[]).filter((data) => data.value !== null || data.description !== null || data.percent !== null);
      const triggerSpectral = isInternal || direction !== "backward"

      dispatch(putIncomeAttributesBySnapshotId(Number(sessionStorage.getItem("currentSnapshotId") as string), {
        "attributes": incomeAttributes,
        "spectral_section": profileOverview.profile.profile_type_code.toLowerCase() + "-" + menuState.activeItem.name,
        "trigger_spectral": triggerSpectral,
        "is_internal": isInternal
      }));
    }
  }, [dispatch, incomeAttributesState.data, menuState.activeItem.name, profileOverview.profile.profile_type_code, saveData.changesPending, saveData.saving, t, isInternal, direction, clearErrors])

  useEffect(() => {
    if (saveResultState.status === 200) {
      dispatch(getIncomeAttributesBySnapshotId(Number(sessionStorage.getItem("currentSnapshotId") as string)));
    }
  }, [dispatch, saveResultState]);

  const handleTotalExpense = (newAmt: number | undefined | null, name: string, oldAmt: number | undefined | null) => {
    let calculated = false;
    let value = 0;
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const na = (newAmt === undefined || newAmt === null) ? null : newAmt
      if (incomeAttributesData) {
        incomeAttributesData["value"] = na;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
          code: code,
          description: null,
          value: na,
          type: "2",
          percent: null,
          year: null,
        });
      }
    }

    if (idx === -1) {
      return;
    }

    const code = name.substring(idx + 1);

    const lookupGroups = lookupState.lookups[2] as Record<string, IncomeAttributeLookupObject[]> | undefined;
    const lookupKeys = lookupGroups ? Object.keys(lookupGroups) : [];
    const hasNonOperatingGroup = lookupKeys.some((key) => nonOperatingKeys.has(key));
    let matchedKey: string | undefined = undefined;

    for (const key of lookupKeys) {
      if (lookupGroups && lookupGroups[key].some((attribute) => attribute.incomeAttributeCode === code)) {
        matchedKey = key;
        break;
      }
    }

    const isNonOperating = hasNonOperatingGroup && matchedKey && nonOperatingKeys.has(matchedKey);
    const currentTotal = isNonOperating ? totalNonOperatingExpense : totalOperatingExpense;

    if (typeof currentTotal === "number") {
      value = currentTotal;
    }
    if (typeof newAmt === "number") {
      value = value + newAmt;
      calculated = true;
    }
    if (typeof oldAmt === "number") {
      value = value - oldAmt;
      calculated = true;
    }
    if (calculated) {
      if (isNonOperating) {
        setTotalNonOperatingExpense(value);
      } else {
        setTotalOperatingExpense(value);
      }
    }
  }

  const handleCommentChange = (value: string, name: string) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const v = value.trim() === "" ? null : value.trim();
      if (incomeAttributesData) {
        incomeAttributesData["description"] = v;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
          code: code,
          description: v,
          value: null,
          type: "2",
          percent: null,
          year: null,
        });
      }
    }
  }

  useEffect(() => {
    if (!lookupState.success) {
      dispatch(getLookups(lookupType));
    }
  }, [dispatch, lookupState.success, lookupType]);

  const getTotals = (year: string, previousYearTotal: boolean) => {
    let operatingTotal = 0;
    let nonOperatingTotal = 0;

    const lookupStateLookups2 = lookupState.lookups[2] as Record<string, IncomeAttributeLookupObject[]>
    if (menuState.activeItem.hasTabs && tabList) {
      const tabKeySet = new Set(tabList.map((tab) => tab.name));
      const keys = Object.keys(lookupStateLookups2);
      keys.forEach((key) => {
        if (!tabKeySet.has(key)) {
          return;
        }
        const lookupAttributes = lookupStateLookups2[key];
        lookupAttributes.forEach((attribute) => {
          if (attribute.activeFlag === true) {
            const code = attribute.incomeAttributeCode;
            let incomeAttributesData = null
            if (previousYearTotal) {
              incomeAttributesData = findPreviousAttributeData(code, String(year));
            } else {
              incomeAttributesData = findAttributeData(code);
            }
            if (incomeAttributesData && incomeAttributesData.value && incomeAttributesData.value !== 0) {
              const value = incomeAttributesData.value as number;
              if (nonOperatingKeys.has(key)) {
                nonOperatingTotal += value;
              } else {
                operatingTotal += value;
              }
            }
          }
        });
      });
    } else {
      const lookupAttributes = lookupStateLookups2[menuState.activeItem.name];
      if (lookupAttributes) {
        lookupAttributes.forEach((attribute) => {
          if (attribute.activeFlag === true) {
            const code = attribute.incomeAttributeCode;
            const incomeAttributesData = findAttributeData(code);
            if (incomeAttributesData && incomeAttributesData.value && incomeAttributesData.value !== 0) {
              operatingTotal += incomeAttributesData.value as number;
            }
          }
        });
      }
    }
    return [operatingTotal, nonOperatingTotal]
  }

  useEffect(() => {
    if (lookupState.success && incomeAttributesState.success) {
      const year = getCurrentCampaignYear()
      const currentTotal = getTotals(String(year), false)
      let tempPreviousOperatingExpenses = {}
      let tempPreviousNonOperatingExpenses = {}
      let previousTotals = getTotals(String(year - 1), true)

      tempPreviousOperatingExpenses = { ...tempPreviousOperatingExpenses, [year - 1]: previousTotals[0] }
      tempPreviousNonOperatingExpenses = { ...tempPreviousNonOperatingExpenses, [year - 1]: previousTotals[1] }

      previousTotals = getTotals(String(year - 2), true)

      tempPreviousOperatingExpenses = { ...tempPreviousOperatingExpenses, [year - 2]: previousTotals[0] }
      tempPreviousNonOperatingExpenses = { ...tempPreviousNonOperatingExpenses, [year - 2]: previousTotals[1] }

      setPreviousTotalOperatingExpenses(tempPreviousOperatingExpenses)
      setPreviousTotalNonOperatingExpenses(tempPreviousNonOperatingExpenses)

      setTotalOperatingExpense(currentTotal[0] || undefined);
      setTotalNonOperatingExpense(currentTotal[1] || undefined);
    }
    // eslint-disable-next-line
  }, [incomeAttributesState.success, lookupState.lookups, lookupState.success, menuState.activeItem, tabList]);

  return (
    <div className='full-width'>
      <div className='content-container'>
        <ErrorMessages errors={errors} />
        <p className='strong'>{t('expenses.caption1', { ns: 'pie' })}</p>
        {
          (menuState.activeItem.hasTabs && tabList) ?
            <>
              <div className='accordion-root'>
                <div className={accordionDisplay(isInternal, "accordion")}>
                  <ControlledAccordion providerValue={providerValue}>
                    {tabList.map(tab => {
                      return <AccordionItem header={t((tab.name === "expenses" && lookupType === "rho-income-attributes") ? "operatingExpenses" : (tab.name === "fixedCharges" && ["ltc-income-attributes", "rho-income-attributes", "mtl-income-attributes"].includes(lookupType) ? "GLF.charges" : tab.name), { ns: 'navigation' })}
                        key={tab.name}
                        itemKey={tab.name}
                      >
                        <ExpenseTab handleTotalExpense={handleTotalExpense}
                          handleCommentChange={handleCommentChange}
                          lookups={lookupState.lookups[2] ? (lookupState.lookups[2] as Record<string, IncomeAttributeLookupObject[]>)[tab.name] : []}
                          incomeAttributes={(incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0) ? (incomeAttributesState.data as Record<string, unknown>[]) : undefined}
                        />
                      </AccordionItem>
                    })}
                  </ControlledAccordion>
                </div>
                {shouldDisplayPreviousYearAttributes() ? <PreviousCampaignIncomeAttributes tabComponent='expense' tabList={tabList} lookupType={lookupType} year={String(currentCampaignYear - 1)} /> : <></>}
                {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <PreviousCampaignIncomeAttributes tabComponent='expense' tabList={tabList} lookupType={lookupType} year={String(currentCampaignYear - 2)} /> : <></>}
              </div>
            </>
            :
            <>
              <ExpenseTab handleTotalExpense={handleTotalExpense}
                lookups={lookupState.lookups[2] ? (lookupState.lookups[2] as Record<string, IncomeAttributeLookupObject[]>)[menuState.activeItem.name] : []}
                handleCommentChange={handleCommentChange}
                incomeAttributes={(incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0) ? (incomeAttributesState.data as Record<string, unknown>[]) : undefined}
              />
            </>
        }
      </div>
      <div className='total-container'>
        <div className="accordion-root">
          <div className={accordionDisplay(isInternal, "accordion")}>
            {(["htl-income-attributes", "mtl-income-attributes", "ltc-income-attributes", "rho-income-attributes", "glf-income-attributes", "bip-income-attributes", "lle-income-attributes"].includes(lookupType)) ? (
              <>
                <Total text={t(lookupType === "glf-income-attributes" ? 'GLF.analysis.totalOperatingExpenses' : 'analysis.totalOperatingExpenses', { ns: 'pie' })} value={totalOperatingExpense} label={true} narrow={(shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? true : false} />
                <Total text={t(lookupType === "glf-income-attributes" ? 'GLF.analysis.totalNonOperatingExpenses' : 'analysis.totalNonOperatingExpenses', { ns: 'pie' })} value={totalNonOperatingExpense} className='secondary' label={true} narrow={(shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? true : false} />
              </>
            ) : (
              <Total text={t('expenses.totalExpenses', { ns: 'pie' })} value={totalOperatingExpense} narrow={(shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? true : false} />
            )}
          </div>
          {shouldDisplayPreviousYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
            {(["htl-income-attributes", "mtl-income-attributes", "ltc-income-attributes", "rho-income-attributes", "glf-income-attributes", "bip-income-attributes", "lle-income-attributes"].includes(lookupType)) ? (
              <>
                <Total text={t(lookupType === "glf-income-attributes" ? 'GLF.analysis.totalOperatingExpenses' : 'analysis.totalOperatingExpenses', { ns: 'pie' })} value={previousTotalOperatingExpenses[String(currentCampaignYear - 1)] || undefined} />
                <Total text={t(lookupType === "glf-income-attributes" ? 'GLF.analysis.totalNonOperatingExpenses' : 'analysis.totalNonOperatingExpenses', { ns: 'pie' })} value={previousTotalNonOperatingExpenses[String(currentCampaignYear - 1)] || undefined} className='secondary' />
              </>
            ) : (
              <Total text={t('expenses.totalExpenses', { ns: 'pie' })} value={totalOperatingExpense} />
            )}
          </div> : <></>}
          {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
            {(["htl-income-attributes", "mtl-income-attributes", "ltc-income-attributes", "rho-income-attributes", "glf-income-attributes", "bip-income-attributes", "lle-income-attributes"].includes(lookupType)) ? (
              <>
                <Total text={t(lookupType === "glf-income-attributes" ? 'GLF.analysis.totalOperatingExpenses' : 'analysis.totalOperatingExpenses', { ns: 'pie' })} value={previousTotalOperatingExpenses[String(currentCampaignYear - 2)] || undefined} />
                <Total text={t(lookupType === "glf-income-attributes" ? 'GLF.analysis.totalNonOperatingExpenses' : 'analysis.totalNonOperatingExpenses', { ns: 'pie' })} value={previousTotalNonOperatingExpenses[String(currentCampaignYear - 2)] || undefined} className='secondary' />
              </>
            ) : (
              <Total text={t('expenses.totalExpenses', { ns: 'pie' })} value={totalOperatingExpense} />
            )}
          </div> : <></>}
        </div>
      </div>
      {((!(menuState.activeItem.name === "fixedCharges") && (menuState.activeItem.name === "expenses" && menuState.activeItem.selectedTabIndex === 0))) && <p><Asterisk /> {t('revenues.atLeaseOneRequired', { ns: 'pie' })}</p>}
    </div>);
}